import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import type { Position, Location, TimeEntry, MonthlyTarget, YearlyVacation, MonthlyVacation, EntryPosition, MonthlyReport, Profile, UserPositionMonthlyPlan, MonthAttachment, UserWorkPercentage, TimeEntryAudit } from '../types';
import { calculateTotalWorkMinutes } from '../utils/timeCalculator';

interface DataContextType {
  positions: Position[];
  projects: Position[]; // Backward-compat alias
  locations: Location[];
  entries: TimeEntry[];
  projectBudgets: Position[]; // Backward-compat alias
  userPositionMonthlyPlans: UserPositionMonthlyPlan[];
  userProjectMonthlyPlans: UserPositionMonthlyPlan[]; // Backward-compat alias
  monthlyTargets: MonthlyTarget[];
  yearlyVacations: YearlyVacation[];
  monthlyVacations: MonthlyVacation[];
  monthlyReports: MonthlyReport[];
  monthAttachments: MonthAttachment[];
  timeEntryAudits: TimeEntryAudit[];
  userProfile: Profile | null;
  settings: { mainPositionId: string; mainProjectId: string };
  loadingData: boolean;

  savePosition: (position: Omit<Position, 'id'> & { id?: string }, memberUserIds?: string[]) => Promise<void>;
  deletePosition: (id: string) => Promise<void>;
  updatePosition: (id: string, name: string, standardText: string, visible?: boolean) => Promise<void>;
  updatePositionStandardText: (id: string, standardText: string, visible?: boolean) => Promise<void>;
  updateProject: (id: string, name: string, standardText: string, visible: boolean) => Promise<void>;
  createProject: (name: string) => Promise<Position>;
  renameProject: (id: string, name: string) => Promise<void>;
  createLocation: (name: string) => Promise<Location>;
  renameLocation: (id: string, name: string) => Promise<void>;
  updateSettings: (mainPositionId: string) => Promise<void>;
  saveEntry: (entry: TimeEntry) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  updateUserPositionMonthlyPlan: (positionId: string, month: string, plannedHours: number) => Promise<void>;
  updateUserProjectMonthlyPlan: (positionId: string, month: string, plannedHours: number) => Promise<void>;
  updateProjectBudget: (positionId: string, month: string, plannedHours: number) => Promise<void>;
  saveProjectBudget: (budget: Omit<Position, 'id'> & { id?: string }, memberUserIds?: string[]) => Promise<void>;
  deleteProjectBudget: (id: string) => Promise<void>;
  fetchPositionEntries: (positionId?: string, startDate?: string, endDate?: string) => Promise<TimeEntry[]>;
  fetchBudgetEntries: (positionId?: string, startDate?: string, endDate?: string) => Promise<TimeEntry[]>;
  updateMonthlyTarget: (month: string, targetDays: number) => Promise<void>;
  updateYearlyVacation: (year: string, totalDays: number) => Promise<void>;
  updateMonthlyVacation: (month: string, plannedDays: number) => Promise<void>;

  completeMonth: (month: string, completed: boolean) => Promise<void>;
  reviewMonth: (userId: string, month: string, reviewed: boolean) => Promise<void>;
  fetchUserEntries: (userId: string) => Promise<TimeEntry[]>;
  fetchUserMonthlyTargets: (userId: string) => Promise<MonthlyTarget[]>;
  fetchUserWorkPercentages: (userId: string) => Promise<UserWorkPercentage[]>;
  updateUserWorkPercentages: (userId: string, percentages: { month: string; percentage: number }[]) => Promise<void>;
  fetchProfiles: () => Promise<Profile[]>;
  uploadMonthAttachment: (month: string, file: File) => Promise<MonthAttachment>;
  deleteMonthAttachment: (id: string) => Promise<void>;
  updateProfileMasterData: (
    userId: string,
    data: {
      employeeNumber?: string | null;
      initials?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      picture?: string | null;
    }
  ) => Promise<void>;
  updateProfileEmployeeData: (userId: string, employeeNumber: string | null, initials: string | null) => Promise<void>;
}

export const DataContext = createContext<DataContextType | null>(null);

interface DbUserProjectSetting {
  project_id: string;
  standard_text?: string | null;
  visible?: boolean | null;
}

interface DbBudgetMember {
  budget_id: string;
  user_id: string;
}

interface DbProject {
  id: string;
  name: string;
  contract_id?: string | null;
  customer_id?: string | null;
  customer_department_id?: string | null;
  service_type?: string | null;
  contract?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  budget_days?: number | string | null;
  warning_threshold_percent?: number | string | null;
  is_global?: boolean | null;
  break_relevant?: boolean | null;
  sub_types?: string[] | null;
}

interface DbEntryProject {
  positionId?: string;
  projectId?: string;
  minutes?: number;
  percentage?: number;
  description?: string;
}

interface DbMonthlyPlan {
  id: string;
  user_id: string;
  project_id: string;
  month: string;
  planned_hours: number | string;
}

interface DbMonthAttachment {
  id: string;
  user_id: string;
  month: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  onedrive_web_url: string;
  onedrive_item_id: string;
  created_at: string;
}

interface DbProfile {
  id: string;
  email: string;
  role: 'user' | 'supervisor';
  employee_number?: string | null;
  initials?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  picture?: string | null;
}

interface DbUserWorkPercentage {
  id: string;
  user_id: string;
  month: string;
  percentage: number | string;
  created_at?: string;
  updated_at?: string;
}

interface DbTimeEntryAudit {
  id: string;
  entry_id: string;
  target_user_id: string;
  changed_by: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  old_data?: Record<string, unknown> | null;
  new_data?: Record<string, unknown> | null;
  created_at: string;
}

export const DataProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();

  const [positions, setPositions] = useState<Position[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [userPositionMonthlyPlans, setUserPositionMonthlyPlans] = useState<UserPositionMonthlyPlan[]>([]);
  const [monthlyTargets, setMonthlyTargets] = useState<MonthlyTarget[]>([]);
  const [yearlyVacations, setYearlyVacations] = useState<YearlyVacation[]>([]);
  const [monthlyVacations, setMonthlyVacations] = useState<MonthlyVacation[]>([]);
  const [monthlyReports, setMonthlyReports] = useState<MonthlyReport[]>([]);
  const [monthAttachments, setMonthAttachments] = useState<MonthAttachment[]>([]);
  const [timeEntryAudits, setTimeEntryAudits] = useState<TimeEntryAudit[]>([]);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [settings, setSettings] = useState({ mainPositionId: '', mainProjectId: '' });
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!user) {
      setPositions([]);
      setLocations([]);
      setEntries([]);
      setUserPositionMonthlyPlans([]);
      setMonthlyTargets([]);
      setYearlyVacations([]);
      setMonthlyVacations([]);
      setMonthlyReports([]);
      setMonthAttachments([]);
      setTimeEntryAudits([]);
      setUserProfile(null);
      setSettings({ mainPositionId: '', mainProjectId: '' });
      setLoadingData(false);
      return;
    }

    const fetchAllLocations = async (): Promise<Location[]> => {
      const all: Location[] = [];
      const pageSize = 1000;
      let page = 0;
      while (true) {
        const from = page * pageSize;
        const to = from + pageSize - 1;
        const { data, error } = await supabase
          .from('locations')
          .select('*')
          .order('name', { ascending: true })
          .range(from, to);

        if (error) {
          console.error('Error fetching locations page', page, error);
          break;
        }
        if (!data || data.length === 0) break;
        all.push(...data);
        if (data.length < pageSize) break;
        page++;
      }
      return all;
    };

    const fetchData = async () => {
      setLoadingData(true);

      const [
        projRes,
        allLocations,
        userProjRes,
        setRes,
        entRes,
        budMemRes,
        planRes,
        targetRes,
        yrVacRes,
        moVacRes,
        repRes,
        attRes,
        profRes,
        auditRes
      ] = await Promise.all([
        supabase.from('projects').select('*').order('id', { ascending: true }),
        fetchAllLocations(),
        supabase.from('user_project_settings').select('*'),
        supabase.from('global_settings').select('*').single(),
        supabase.from('time_entries').select('*'),
        supabase.from('project_budget_members').select('*'),
        supabase.from('user_project_monthly_plans').select('*'),
        supabase.from('monthly_targets').select('*'),
        supabase.from('yearly_vacation_allowance').select('*'),
        supabase.from('monthly_vacation_planned').select('*'),
        supabase.from('monthly_reports').select('*'),
        supabase.from('month_attachments').select('*'),
        supabase.from('profiles').select('*').eq('id', user.id).maybeSingle(),
        supabase.from('time_entry_audits').select('*').order('created_at', { ascending: false })
      ]);

      if (projRes.data) {
        const settingsMap = new Map<string, { standardText: string; visible: boolean }>();
        if (userProjRes.data) {
          (userProjRes.data as unknown as DbUserProjectSetting[]).forEach(s => {
            settingsMap.set(s.project_id, {
              standardText: s.standard_text || '',
              visible: s.visible ?? true
            });
          });
        }

        // Map members from project_budget_members
        const membersByPosition = new Map<string, string[]>();
        if (budMemRes.data) {
          (budMemRes.data as unknown as DbBudgetMember[]).forEach(m => {
            const list = membersByPosition.get(m.budget_id) || [];
            list.push(m.user_id);
            membersByPosition.set(m.budget_id, list);
          });
        }

        setPositions((projRes.data as unknown as DbProject[]).map(p => {
          const userSet = settingsMap.get(p.id);
          const contingentDays = Number(p.budget_days ?? 0);
          const members = membersByPosition.get(p.id) || [];
          const isGlobal = Boolean(p.is_global ?? false);

          return {
            id: p.id,
            name: p.name,
            contractId: p.contract_id || '',
            customerId: p.customer_id || '',
            customerDepartmentId: p.customer_department_id || '',
            serviceType: p.service_type || '',
            contract: p.contract || '',
            startDate: p.start_date || undefined,
            endDate: p.end_date || undefined,
            contingentDays,
            budgetDays: contingentDays,
            warningThresholdPercent: Number(p.warning_threshold_percent ?? 80),
            isGlobal,
            breakRelevant: Boolean(p.break_relevant ?? false),
            members,
            standardText: userSet?.standardText || '',
            visible: userSet?.visible ?? true,
            subTypes: Array.isArray(p.sub_types) ? p.sub_types : []
          };
        }));
      }

      if (allLocations) {
        setLocations([...allLocations].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })));
      }
      if (setRes.data) {
        const mId = setRes.data.main_project_id || '';
        setSettings({ mainPositionId: mId, mainProjectId: mId });
      }

      if (entRes.data) {
        const mappedEntries = entRes.data.map(e => {
          const totalMins = calculateTotalWorkMinutes(e.time_arrived, e.time_left, e.break_duration_minutes);
          const rawProjects = (e.projects || []) as unknown as DbEntryProject[];
          const mappedProjects = rawProjects.map(p => {
            let mins = p.minutes;
            if (typeof mins !== 'number' && typeof p.percentage === 'number') {
              mins = Math.round((totalMins * (p.percentage / 100)) / 15) * 15;
            }
            const posId = p.positionId || p.projectId || '';
            const ep: EntryPosition = {
              positionId: posId,
              projectId: posId,
              percentage: p.percentage,
              minutes: mins || 0,
              description: p.description || ''
            };
            return ep;
          });

          const mPosId = e.main_project_id || '';
          return {
            id: e.id,
            userId: e.user_id,
            date: e.date,
            timeArrived: e.time_arrived,
            timeLeft: e.time_left,
            breakDurationMinutes: e.break_duration_minutes,
            locationId: e.location_id,
            mainPositionId: mPosId,
            mainProjectId: mPosId,
            positions: mappedProjects,
            projects: mappedProjects
          };
        });
        setEntries(mappedEntries);
      }

      // Map user monthly plans
      if (planRes.data) {
        setUserPositionMonthlyPlans((planRes.data as unknown as DbMonthlyPlan[]).map(p => ({
          id: p.id,
          userId: p.user_id,
          positionId: p.project_id,
          projectId: p.project_id,
          month: p.month,
          plannedHours: Number(p.planned_hours)
        })));
      }

      if (targetRes.data) {
        setMonthlyTargets(targetRes.data.map(t => ({
          id: t.id,
          month: t.month,
          targetDays: Number(t.target_days)
        })));
      }

      if (yrVacRes.data) {
        setYearlyVacations(yrVacRes.data.map(v => ({
          id: v.id,
          year: v.year,
          totalDays: Number(v.total_days)
        })));
      }

      if (moVacRes.data) {
        setMonthlyVacations(moVacRes.data.map(v => ({
          id: v.id,
          month: v.month,
          plannedDays: Number(v.planned_days)
        })));
      }

      if (repRes.data) {
        setMonthlyReports(repRes.data.map(r => ({
          id: r.id,
          userId: r.user_id,
          month: r.month,
          completed: r.completed,
          completedAt: r.completed_at,
          reviewed: r.reviewed,
          reviewedAt: r.reviewed_at,
          reviewedBy: r.reviewed_by
        })));
      }

      if (attRes.data) {
        setMonthAttachments((attRes.data as unknown as DbMonthAttachment[]).map(a => ({
          id: a.id,
          userId: a.user_id,
          month: a.month,
          fileName: a.file_name,
          fileSize: a.file_size,
          mimeType: a.mime_type,
          onedriveWebUrl: a.onedrive_web_url,
          onedriveItemId: a.onedrive_item_id,
          createdAt: a.created_at
        })));
      }

      if (auditRes?.data) {
        setTimeEntryAudits((auditRes.data as unknown as DbTimeEntryAudit[]).map(a => ({
          id: a.id,
          entryId: a.entry_id,
          targetUserId: a.target_user_id,
          changedBy: a.changed_by,
          action: a.action,
          oldData: a.old_data,
          newData: a.new_data,
          createdAt: a.created_at
        })));
      }

      if (user) {
        let currentProf = profRes?.data;

        // Auto-populate Microsoft SSO user profile if attributes are missing
        const meta = (user.user_metadata || {}) as Record<string, unknown>;
        const appMeta = (user.app_metadata || {}) as Record<string, unknown>;
        const identities = (user.identities || []) as Array<{ provider?: string }>;
        const isAzure = appMeta.provider === 'azure' ||
          (Array.isArray(appMeta.providers) && appMeta.providers.includes('azure')) ||
          identities.some(i => i.provider === 'azure');

        const ssoFirst = (meta.given_name || meta.first_name || (
          typeof meta.full_name === 'string' ? meta.full_name.split(' ')[0] : (typeof meta.name === 'string' ? meta.name.split(' ')[0] : null)
        )) as string | null;
        const ssoLast = (meta.family_name || meta.last_name || (
          typeof meta.full_name === 'string' && meta.full_name.includes(' ')
            ? meta.full_name.substring(meta.full_name.indexOf(' ') + 1).trim()
            : (typeof meta.name === 'string' && meta.name.includes(' ') ? meta.name.substring(meta.name.indexOf(' ') + 1).trim() : null)
        )) as string | null;

        if (isAzure || ssoFirst || ssoLast) {
          if (!currentProf) {
            const { data: newProf, error: insErr } = await supabase
              .from('profiles')
              .upsert({
                id: user.id,
                email: user.email || '',
                role: 'user',
                first_name: ssoFirst || null,
                last_name: ssoLast || null
              })
              .select()
              .single();
            if (!insErr && newProf) {
              currentProf = newProf;
            }
          } else {
            const updatesToApply: Record<string, string> = {};
            if (!currentProf.first_name && ssoFirst) updatesToApply.first_name = ssoFirst;
            if (!currentProf.last_name && ssoLast) updatesToApply.last_name = ssoLast;

            if (Object.keys(updatesToApply).length > 0) {
              const { data: updatedProf, error: updErr } = await supabase
                .from('profiles')
                .update(updatesToApply)
                .eq('id', user.id)
                .select()
                .single();
              if (!updErr && updatedProf) {
                currentProf = updatedProf;
              } else {
                currentProf = { ...currentProf, ...updatesToApply };
              }
            }
          }
        }

        if (currentProf) {
          setUserProfile({
            id: currentProf.id,
            email: currentProf.email,
            role: currentProf.role,
            employeeNumber: currentProf.employee_number || null,
            initials: currentProf.initials || null,
            firstName: currentProf.first_name || null,
            lastName: currentProf.last_name || null,
            picture: currentProf.picture || null
          });
        }
      }

      setLoadingData(false);
    };

    fetchData();
  }, [user]);

  const updatePosition = async (id: string, name: string, standardText: string, visible: boolean = true): Promise<void> => {
    const previousPositions = positions;
    setPositions(prev => prev.map(p => p.id === id ? { ...p, name, standardText, visible } : p));

    const { error } = await supabase.from('user_project_settings')
      .upsert({
        user_id: user?.id,
        project_id: id,
        standard_text: standardText,
        visible: visible
      }, { onConflict: 'user_id,project_id' });

    if (error) {
      setPositions(previousPositions);
      throw error;
    }
  };

  const updatePositionStandardText = async (id: string, standardText: string, visible: boolean = true) => {
    return updatePosition(id, positions.find(p => p.id === id)?.name || '', standardText, visible);
  };

  const updateProject = updatePosition;

  const createProject = async (name: string): Promise<Position> => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Position name cannot be empty');

    const { data, error } = await supabase
      .from('projects')
      .insert({ name: trimmed })
      .select()
      .single();

    if (error) throw error;

    const newPosition: Position = {
      id: data.id,
      name: data.name,
      contractId: '',
      customerId: '',
      customerDepartmentId: '',
      serviceType: '',
      contract: '',
      contingentDays: 0,
      budgetDays: 0,
      warningThresholdPercent: 80,
      isGlobal: false,
      breakRelevant: false,
      members: [],
      standardText: '',
      visible: true,
      subTypes: []
    };

    setPositions(prev => [...prev, newPosition]);
    return newPosition;
  };

  const renameProject = async (id: string, name: string): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Position name cannot be empty');

    const { error } = await supabase
      .from('projects')
      .update({ name: trimmed })
      .eq('id', id);

    if (error) throw error;

    setPositions(prev => prev.map(p => p.id === id ? { ...p, name: trimmed } : p));
  };

  const createLocation = async (name: string): Promise<Location> => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Location name cannot be empty');

    const { data, error } = await supabase
      .from('locations')
      .insert({ name: trimmed })
      .select()
      .single();

    if (error) throw error;

    const newLoc: Location = {
      id: data.id,
      name: data.name
    };

    setLocations(prev =>
      [...prev, newLoc].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }))
    );
    return newLoc;
  };

  const renameLocation = async (id: string, name: string): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Location name cannot be empty');

    const { error } = await supabase
      .from('locations')
      .update({ name: trimmed })
      .eq('id', id);

    if (error) throw error;

    setLocations(prev =>
      prev.map(l => l.id === id ? { ...l, name: trimmed } : l).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }))
    );
  };

  const updateSettings = async (mainPositionId: string) => {
    const { error } = await supabase.from('global_settings')
      .upsert({ user_id: user?.id, main_project_id: mainPositionId });
    if (error) throw error;
    setSettings({ mainPositionId, mainProjectId: mainPositionId });
  };

  const saveEntry = async (entry: TimeEntry) => {
    const mainPosId = entry.mainPositionId || entry.mainProjectId;
    const activePositions = (entry.positions || entry.projects || []).map(p => {
      const posId = p.positionId || p.projectId;
      return {
        projectId: posId,
        positionId: posId,
        minutes: p.minutes,
        description: p.description
      };
    });

    const dbEntry = {
      id: entry.id,
      user_id: entry.userId || user?.id,
      date: entry.date,
      time_arrived: entry.timeArrived,
      time_left: entry.timeLeft,
      break_duration_minutes: Math.round(entry.breakDurationMinutes || 0),
      location_id: entry.locationId,
      main_project_id: mainPosId || null,
      projects: activePositions
    };

    const { error } = await supabase.from('time_entries').upsert(dbEntry);
    if (error) throw error;

    const normalizedEntry: TimeEntry = {
      ...entry,
      breakDurationMinutes: Math.round(entry.breakDurationMinutes || 0),
      userId: entry.userId || user?.id,
      mainPositionId: mainPosId,
      mainProjectId: mainPosId,
      positions: activePositions,
      projects: activePositions
    };

    setEntries(prev => {
      const exists = prev.find(e => e.id === entry.id);
      if (exists) {
        return prev.map(e => e.id === entry.id ? normalizedEntry : e);
      }
      return [...prev, normalizedEntry];
    });
  };

  const deleteEntry = async (id: string) => {
    const { error } = await supabase.from('time_entries').delete().eq('id', id);
    if (error) throw error;
    setEntries(entries.filter(e => e.id !== id));
  };

  const updateUserPositionMonthlyPlan = async (positionId: string, month: string, plannedHours: number) => {
    const dbPayload = {
      user_id: user?.id,
      project_id: positionId,
      month: month,
      planned_hours: plannedHours
    };

    const { data, error } = await supabase.from('user_project_monthly_plans')
      .upsert(dbPayload, { onConflict: 'user_id,project_id,month' })
      .select().single();

    if (error) throw error;

    if (data) {
      setUserPositionMonthlyPlans(prev => {
        const exists = prev.find(b => b.positionId === positionId && b.month === month);
        const newPlan = { id: data.id, userId: data.user_id, positionId: data.project_id, projectId: data.project_id, month: data.month, plannedHours: Number(data.planned_hours) };
        if (exists) return prev.map(b => b.positionId === positionId && b.month === month ? newPlan : b);
        return [...prev, newPlan];
      });
    }
  };

  const updateUserProjectMonthlyPlan = updateUserPositionMonthlyPlan;
  const updateProjectBudget = updateUserPositionMonthlyPlan;

  const savePosition = async (
    position: Omit<Position, 'id'> & { id?: string },
    memberUserIds: string[] = []
  ) => {
    const payload: {
      name: string;
      contract_id: string;
      customer_id: string;
      customer_department_id: string;
      service_type: string;
      contract: string;
      start_date: string | null;
      end_date: string | null;
      budget_days: number;
      warning_threshold_percent: number;
      is_global: boolean;
      break_relevant: boolean;
      sub_types?: string[];
      id?: string;
    } = {
      name: position.name.trim(),
      contract_id: position.contractId?.trim() || '',
      customer_id: position.customerId?.trim() || '',
      customer_department_id: position.customerDepartmentId?.trim() || '',
      service_type: position.serviceType?.trim() || '',
      contract: position.contract?.trim() || '',
      start_date: position.startDate || null,
      end_date: position.endDate || null,
      budget_days: position.contingentDays ?? position.budgetDays ?? 0,
      warning_threshold_percent: position.warningThresholdPercent ?? 80,
      is_global: position.isGlobal ?? false,
      break_relevant: position.breakRelevant ?? false,
      sub_types: position.subTypes || []
    };
    if (position.id) {
      payload.id = position.id;
    }

    let posData: { id: string } | null;
    const { data: upsertData, error: posError } = await supabase
      .from('projects')
      .upsert(payload)
      .select()
      .single();

    if (posError) {
      if (posError.message?.includes('sub_types') || posError.details?.includes('sub_types')) {
        delete payload.sub_types;
        const { data: retryData, error: retryError } = await supabase
          .from('projects')
          .upsert(payload)
          .select()
          .single();
        if (retryError) throw retryError;
        posData = retryData;
      } else {
        throw posError;
      }
    } else {
      posData = upsertData;
    }

    const positionId = position.id || posData?.id;

    if (positionId) {
      // Update members
      await supabase.from('project_budget_members').delete().eq('budget_id', positionId);

      if (memberUserIds.length > 0) {
        const memberPayloads = memberUserIds.map(uid => ({
          budget_id: positionId,
          user_id: uid
        }));
        const { error: memberError } = await supabase.from('project_budget_members').insert(memberPayloads);
        if (memberError) console.error('Error inserting position members:', memberError);
      }

      const contingentDays = position.contingentDays ?? position.budgetDays ?? 0;
      const updatedPosition: Position = {
        id: positionId,
        name: position.name.trim(),
        contractId: position.contractId?.trim() || '',
        customerId: position.customerId?.trim() || '',
        customerDepartmentId: position.customerDepartmentId?.trim() || '',
        serviceType: position.serviceType?.trim() || '',
        contract: position.contract?.trim() || '',
        startDate: position.startDate,
        endDate: position.endDate,
        contingentDays,
        budgetDays: contingentDays,
        warningThresholdPercent: position.warningThresholdPercent ?? 80,
        isGlobal: position.isGlobal ?? false,
        breakRelevant: position.breakRelevant ?? false,
        members: memberUserIds,
        standardText: position.standardText || '',
        visible: position.visible ?? true,
        subTypes: position.subTypes || []
      };

      setPositions(prev => {
        const exists = prev.some(p => p.id === positionId);
        if (exists) return prev.map(p => p.id === positionId ? updatedPosition : p);
        return [...prev, updatedPosition];
      });
    }
  };

  const saveProjectBudget = savePosition;

  const deletePosition = async (id: string) => {
    // 1. Clear any main_project_id references in global_settings to avoid FK violation
    const { error: settingsErr } = await supabase
      .from('global_settings')
      .update({ main_project_id: null })
      .eq('main_project_id', id);
    if (settingsErr) console.warn('Warning updating global_settings on position delete:', settingsErr);

    // 2. Clean up user settings and monthly plans for this position
    await supabase.from('user_project_settings').delete().eq('project_id', id);
    await supabase.from('user_project_monthly_plans').delete().eq('project_id', id);

    // 3. Clean up any member links
    await supabase.from('project_budget_members').delete().eq('budget_id', id);

    // 4. Delete from projects
    const { error } = await supabase.from('projects').delete().eq('id', id);
    if (error) throw error;

    setPositions(prev => prev.filter(p => p.id !== id));
    setSettings(prev => prev.mainPositionId === id ? { mainPositionId: '', mainProjectId: '' } : prev);
  };

  const deleteProjectBudget = deletePosition;

  const fetchPositionEntries = useCallback(async (positionId?: string, startDate?: string, endDate?: string): Promise<TimeEntry[]> => {
    let query = supabase.from('time_entries').select('*');
    if (startDate) query = query.gte('date', startDate);
    if (endDate) query = query.lte('date', endDate);

    const { data, error } = await query;
    if (error) {
      console.error('Failed to fetch position entries:', error);
      return [];
    }

    const mapped = (data || []).map(e => {
      const totalMins = calculateTotalWorkMinutes(e.time_arrived, e.time_left, e.break_duration_minutes);
      const rawProjects = (e.projects || []) as unknown as DbEntryProject[];
      const mappedProjects = rawProjects.map(p => {
        let mins = p.minutes;
        if (typeof mins !== 'number' && typeof p.percentage === 'number') {
          mins = Math.round((totalMins * (p.percentage / 100)) / 15) * 15;
        }
        const posId = p.positionId || p.projectId || '';
        return {
          positionId: posId,
          projectId: posId,
          percentage: p.percentage,
          minutes: mins || 0,
          description: p.description || ''
        };
      });

      const mPosId = e.main_project_id || '';
      return {
        id: e.id,
        userId: e.user_id,
        date: e.date,
        timeArrived: e.time_arrived,
        timeLeft: e.time_left,
        breakDurationMinutes: e.break_duration_minutes,
        locationId: e.location_id,
        mainPositionId: mPosId,
        mainProjectId: mPosId,
        positions: mappedProjects,
        projects: mappedProjects
      };
    });

    if (positionId) {
      return mapped.filter(e => (e.positions || []).some((p: EntryPosition) => p.positionId === positionId || p.projectId === positionId));
    }
    return mapped;
  }, []);

  const fetchBudgetEntries = fetchPositionEntries;

  const updateMonthlyTarget = async (month: string, targetDays: number) => {
    const dbPayload = {
      user_id: user?.id,
      month: month,
      target_days: targetDays
    };

    const { data, error } = await supabase.from('monthly_targets')
      .upsert(dbPayload, { onConflict: 'user_id,month' })
      .select().single();

    if (error) throw error;

    if (data) {
      setMonthlyTargets(prev => {
        const exists = prev.find(t => t.month === month);
        const newTarget = { id: data.id, month: data.month, targetDays: Number(data.target_days) };
        if (exists) return prev.map(t => t.month === month ? newTarget : t);
        return [...prev, newTarget];
      });
    }
  };

  const updateYearlyVacation = async (year: string, totalDays: number) => {
    const dbPayload = {
      user_id: user?.id,
      year: year,
      total_days: totalDays
    };

    const { data, error } = await supabase.from('yearly_vacation_allowance')
      .upsert(dbPayload, { onConflict: 'user_id,year' })
      .select().single();

    if (error) throw error;

    if (data) {
      setYearlyVacations(prev => {
        const exists = prev.find(v => v.year === year);
        const newVacation = { id: data.id, year: data.year, totalDays: Number(data.total_days) };
        if (exists) return prev.map(v => v.year === year ? newVacation : v);
        return [...prev, newVacation];
      });
    }
  };

  const updateMonthlyVacation = async (month: string, plannedDays: number) => {
    const dbPayload = {
      user_id: user?.id,
      month: month,
      planned_days: plannedDays
    };

    const { data, error } = await supabase.from('monthly_vacation_planned')
      .upsert(dbPayload, { onConflict: 'user_id,month' })
      .select().single();

    if (error) throw error;

    if (data) {
      setMonthlyVacations(prev => {
        const exists = prev.find(v => v.month === month);
        const newVacation = { id: data.id, month: data.month, plannedDays: Number(data.planned_days) };
        if (exists) return prev.map(v => v.month === month ? newVacation : v);
        return [...prev, newVacation];
      });
    }
  };

  const completeMonth = async (month: string, completed: boolean) => {
    const payload = {
      user_id: user?.id,
      month: month,
      completed: completed,
      completed_at: completed ? new Date().toISOString() : null
    };

    const { data, error } = await supabase
      .from('monthly_reports')
      .upsert(payload, { onConflict: 'user_id,month' })
      .select()
      .single();

    if (error) throw error;

    if (data) {
      setMonthlyReports(prev => {
        const exists = prev.find(r => r.month === month && r.userId === user?.id);
        const mappedReport = {
          id: data.id,
          userId: data.user_id,
          month: data.month,
          completed: data.completed,
          completedAt: data.completed_at,
          reviewed: data.reviewed,
          reviewedAt: data.reviewed_at,
          reviewedBy: data.reviewed_by
        };
        if (exists) return prev.map(r => (r.month === month && r.userId === user?.id) ? mappedReport : r);
        return [...prev, mappedReport];
      });
    }
  };

  const reviewMonth = async (userId: string, month: string, reviewed: boolean) => {
    const payload = {
      reviewed: reviewed,
      reviewed_at: reviewed ? new Date().toISOString() : null,
      reviewed_by: user?.id
    };

    // Attempt UPDATE first so PostgreSQL checks the UPDATE RLS policy (allowed for supervisors)
    // instead of the INSERT policy (which fires on upsert statements and fails for foreign user_ids)
    const { data: updateData, error: updateError } = await supabase
      .from('monthly_reports')
      .update(payload)
      .eq('user_id', userId)
      .eq('month', month)
      .select();

    let data = updateData && updateData.length > 0 ? updateData[0] : null;

    if (!data && !updateError) {
      const fullPayload = {
        user_id: userId,
        month: month,
        ...payload
      };
      const { data: upsertData, error: upsertError } = await supabase
        .from('monthly_reports')
        .upsert(fullPayload, { onConflict: 'user_id,month' })
        .select()
        .single();
      if (upsertError) throw upsertError;
      data = upsertData;
    } else if (updateError) {
      throw updateError;
    }

    if (data) {
      setMonthlyReports(prev => {
        const exists = prev.find(r => r.month === month && r.userId === userId);
        const mappedReport = {
          id: data.id,
          userId: data.user_id,
          month: data.month,
          completed: data.completed,
          completedAt: data.completed_at,
          reviewed: data.reviewed,
          reviewedAt: data.reviewed_at,
          reviewedBy: data.reviewed_by
        };
        if (exists) return prev.map(r => (r.month === month && r.userId === userId) ? mappedReport : r);
        return [...prev, mappedReport];
      });
    }
  };

  const fetchUserEntries = async (userId: string): Promise<TimeEntry[]> => {
    const { data, error } = await supabase
      .from('time_entries')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;

    return (data || []).map(e => {
      const totalMins = calculateTotalWorkMinutes(e.time_arrived, e.time_left, e.break_duration_minutes);
      const rawProjects = (e.projects || []) as unknown as DbEntryProject[];
      const mappedProjects = rawProjects.map(p => {
        let mins = p.minutes;
        if (typeof mins !== 'number' && typeof p.percentage === 'number') {
          mins = Math.round((totalMins * (p.percentage / 100)) / 15) * 15;
        }
        const posId = p.positionId || p.projectId || '';
        return {
          positionId: posId,
          projectId: posId,
          percentage: p.percentage,
          minutes: mins || 0,
          description: p.description || ''
        };
      });

      const mPosId = e.main_project_id || '';
      return {
        id: e.id,
        userId: e.user_id,
        date: e.date,
        timeArrived: e.time_arrived,
        timeLeft: e.time_left,
        breakDurationMinutes: e.break_duration_minutes,
        locationId: e.location_id,
        mainPositionId: mPosId,
        mainProjectId: mPosId,
        positions: mappedProjects,
        projects: mappedProjects
      };
    });
  };

  const fetchUserMonthlyTargets = async (userId: string): Promise<MonthlyTarget[]> => {
    const { data, error } = await supabase
      .from('monthly_targets')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;

    return (data || []).map(t => ({
      id: t.id,
      month: t.month,
      targetDays: Number(t.target_days)
    }));
  };

  const fetchUserWorkPercentages = useCallback(async (userId: string): Promise<UserWorkPercentage[]> => {
    const { data, error } = await supabase
      .from('user_work_percentages')
      .select('*')
      .eq('user_id', userId)
      .order('month', { ascending: true });

    if (error) {
      console.error('Failed to fetch user work percentages:', error);
      throw error;
    }

    return ((data || []) as unknown as DbUserWorkPercentage[]).map(row => ({
      id: row.id,
      userId: row.user_id,
      month: row.month,
      percentage: Number(row.percentage)
    }));
  }, []);

  const updateUserWorkPercentages = async (
    userId: string,
    percentages: { month: string; percentage: number }[]
  ): Promise<void> => {
    if (percentages.length === 0) return;

    const payload = percentages.map(p => ({
      user_id: userId,
      month: p.month,
      percentage: p.percentage,
      updated_at: new Date().toISOString()
    }));

    const { error } = await supabase
      .from('user_work_percentages')
      .upsert(payload, { onConflict: 'user_id,month' });

    if (error) {
      console.error('Failed to update user work percentages:', error);
      throw error;
    }
  };

  const fetchProfiles = useCallback(async (): Promise<Profile[]> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*');

    if (error) throw error;

    return ((data || []) as unknown as DbProfile[]).map(p => ({
      id: p.id,
      email: p.email,
      role: p.role,
      employeeNumber: p.employee_number || null,
      initials: p.initials || null,
      firstName: p.first_name || null,
      lastName: p.last_name || null,
      picture: p.picture || null
    }));
  }, []);

  const updateProfileMasterData = async (
    userId: string,
    data: {
      employeeNumber?: string | null;
      initials?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      picture?: string | null;
    }
  ): Promise<void> => {
    const cleanEmp = data.employeeNumber !== undefined ? (data.employeeNumber?.trim() || null) : undefined;
    const cleanInit = data.initials !== undefined ? (data.initials?.trim().toUpperCase() || null) : undefined;
    const cleanFirst = data.firstName !== undefined ? (data.firstName?.trim() || null) : undefined;
    const cleanLast = data.lastName !== undefined ? (data.lastName?.trim() || null) : undefined;
    const cleanPic = data.picture !== undefined ? (data.picture?.trim() || null) : undefined;

    const payload: Record<string, string | null> = {};
    if (cleanEmp !== undefined) payload.employee_number = cleanEmp;
    if (cleanInit !== undefined) payload.initials = cleanInit;
    if (cleanFirst !== undefined) payload.first_name = cleanFirst;
    if (cleanLast !== undefined) payload.last_name = cleanLast;
    if (cleanPic !== undefined) payload.picture = cleanPic;

    const { data: resData, error } = await supabase
      .from('profiles')
      .update(payload)
      .eq('id', userId)
      .select();

    if (error) {
      console.error('Failed to update profile master data:', error);
      throw error;
    }

    const updatedRows = Array.isArray(resData) ? resData : resData ? [resData] : [];
    if (updatedRows.length === 0) {
      const msg = 'No rows were updated in profiles table. Please verify Supabase RLS policy allows supervisors to update profiles.';
      console.warn(msg);
      throw new Error(msg);
    }

    if (user?.id === userId) {
      setUserProfile(prev => prev ? {
        ...prev,
        ...(cleanEmp !== undefined && { employeeNumber: cleanEmp }),
        ...(cleanInit !== undefined && { initials: cleanInit }),
        ...(cleanFirst !== undefined && { firstName: cleanFirst }),
        ...(cleanLast !== undefined && { lastName: cleanLast }),
        ...(cleanPic !== undefined && { picture: cleanPic })
      } : null);
    }
  };

  const updateProfileEmployeeData = async (userId: string, employeeNumber: string | null, initials: string | null): Promise<void> => {
    return updateProfileMasterData(userId, { employeeNumber, initials });
  };

  const uploadMonthAttachment = async (month: string, file: File): Promise<MonthAttachment> => {
    // Read file as base64
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const commaIdx = result.indexOf(',');
        resolve(commaIdx >= 0 ? result.substring(commaIdx + 1) : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const { data, error } = await supabase.functions.invoke('upload-month-attachment', {
      body: {
        month,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/pdf',
        fileBase64: base64,
        employeeNumber: userProfile?.employeeNumber || undefined,
        employeeInitials: userProfile?.initials || undefined
      }
    });

    if (error) {
      throw new Error(error.message || 'Failed to upload attachment');
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    const newAttachment: MonthAttachment = {
      id: data.id,
      userId: data.user_id,
      month: data.month,
      fileName: data.file_name,
      fileSize: data.file_size,
      mimeType: data.mime_type,
      onedriveWebUrl: data.onedrive_web_url,
      onedriveItemId: data.onedrive_item_id,
      createdAt: data.created_at
    };

    setMonthAttachments(prev => [...prev, newAttachment]);
    return newAttachment;
  };

  const deleteMonthAttachment = async (id: string): Promise<void> => {
    const { error } = await supabase.from('month_attachments').delete().eq('id', id);
    if (error) throw error;
    setMonthAttachments(prev => prev.filter(a => a.id !== id));
  };

  return (
    <DataContext.Provider value={{
      positions,
      projects: positions,
      locations,
      entries,
      projectBudgets: positions,
      userPositionMonthlyPlans,
      userProjectMonthlyPlans: userPositionMonthlyPlans,
      monthlyTargets,
      yearlyVacations,
      monthlyVacations,
      monthlyReports,
      monthAttachments,
      timeEntryAudits,
      userProfile,
      settings,
      loadingData,

      savePosition,
      deletePosition,
      updatePosition,
      updatePositionStandardText,
      updateProject,
      createProject,
      renameProject,
      createLocation,
      renameLocation,
      updateSettings,
      saveEntry,
      deleteEntry,
      updateUserPositionMonthlyPlan,
      updateUserProjectMonthlyPlan,
      updateProjectBudget,
      saveProjectBudget,
      deleteProjectBudget,
      fetchPositionEntries,
      fetchBudgetEntries,
      updateMonthlyTarget,
      updateYearlyVacation,
      updateMonthlyVacation,

      completeMonth,
      reviewMonth,
      fetchUserEntries,
      fetchUserMonthlyTargets,
      fetchUserWorkPercentages,
      updateUserWorkPercentages,
      fetchProfiles,
      uploadMonthAttachment,
      deleteMonthAttachment,
      updateProfileMasterData,
      updateProfileEmployeeData
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error("useData must be used within DataProvider");
  return context;
};
